import { flashError } from "@/utils/flash.js";
import * as printess from "@/lib/printess/utils.js";
import Sentry from "@/sentry.js";

const AUTOSAVE_INTERVAL = 20000; // ms;
const METADATA_FIELD_NAME = "metadata";
const PRINTESS_VERSION_PARAM = "printess-version";

export const BaseMixin = (Base) =>
  class extends Base {
    onEditorLoaded() {}

    connect() {
      window.editor = this.editor;
      window._printessBridge = {
        receive: this.handlePrintessBridge.bind(this),
      };

      this.editorReady = false;
      this.onDocumentChangedForProperties = {};
      this.enrichSentryContext();
      this.enableSaveOnSubmit();
      this.loadPrintessEditor();
    }

    disconnect() {
      this.disconnectDuvelMoortgatBeer();
      this.disconnectSwinkelsFamilyBrewery();
    }

    enrichSentryContext() {
      Sentry.setContext("editor", {
        kind: "printess",
        template: this.templateName(),
      });
    }

    //  optional: show alert
    handleError(error) {
      Sentry.captureException(error);
    }

    importerUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      let version;
      if (urlParams.has(PRINTESS_VERSION_PARAM)) {
        // sanitize it
        version = urlParams
          .get(PRINTESS_VERSION_PARAM)
          .replace(/[^a-zA-Z0-9-_]/g, "")
          .trim();
      }

      if (version) {
        return `https://editor.printess.com/v/${version}/printess-editor/loader.js`;
      }

      return "https://editor.printess.com/printess-editor/loader.js";
    }

    async importLoader() {
      return await (new Function(`return import("${this.importerUrl() }")`))();
    }

    async loadPrintessEditor() {
      const printessLoader = await this.importLoader();
      window.printessLoader = printessLoader;

      this.editor = await printessLoader.load({
        token: this.shopTokenValue,
        templateName: this.templateName(),
        container: this.containerTarget,
        theme: "app.menuez.nl",
        basketId: this.basketIdValue,
        addToBasketCallback: this.addToBasketCallback.bind(this),
        selectionChangeCallback: this.onSelectionChanged.bind(this),
      });

      this.editorReady = true;

      this.onEditorLoaded();

      if (this.sourceMenuDataValue) await this.pushSourceMenu();
      this.pushCss();
      this.attachAutoSaver();
      this.updateDuvelMoortgatBeer();
      this.updateSwinkelsFamilyBrewery();
    }

    onDocumentChangedPushTextVariablesAndLogo(properties) {
        // we already processed a document with this set of properties (a.k.a form fields)
        if (this.onDocumentChangedForProperties[properties]) { return }
        // prevent multiple executions here
        this.onDocumentChangedForProperties[properties] = true
        // Array.wrap the properties
        if (!Array.isArray(properties) && typeof(properties) === "object" && properties !== null) {
          properties = [properties]
        }
        // perform logic that require form fields
        this.pushTextVariables(properties)
        this.uploadLogo(properties)
    }

    onSelectionChanged(properties, scope) {
      // We're not ready yet
      if (!this.editorReady) { return; }
      // We don't have to change anything if there are no properties
      if (!properties) {return;}

      if (scope === "document") {
        try {
          this.onDocumentChangedPushTextVariablesAndLogo(properties);
        } catch (error) { this.handleError(error); }
      }

      try {
        this.onSelectionChangedRenderPalette(properties);
      } catch (error) { this.handleError(error); }

    }

    // Metadata from the template — a special field that we use to store
    // useful things
    async metadata(key = null) {
      const metadataField = await printess.fieldByName(
        this.editor.api,
        METADATA_FIELD_NAME,
      );

      if (!metadataField) return null;

      const data = JSON.parse(metadataField.data.v);

      if (key) {
        return data[key];
      }

      return data;
    }

    // Template will be the template name given if the template was never saved.
    // After customisation, though, it's the save token.
    templateName() {
      if (this.hasSaveToken()) {
        return this.saveTokenInputTarget.value;
      }

      return this.templateNameValue;
    }

    isSaveInProgress() {
      return this.formSubmitButtonTarget.disabled;
    }

    hasUnsavedChanges() {
      return this.editor.api.hasUnsavedChanges();
    }

    disableManualSave() {
      this.formSubmitButtonTarget.disabled = true;
    }

    enableManualSave() {
      this.formSubmitButtonTarget.disabled = false;
    }

    // Make sure we regularly save @ printess and mirror the token @ menuez
    // So that a user will not lose their work in the event of something going wrong.
    attachAutoSaver() {
      setInterval(async () => {
        if (this.isSaveInProgress()) {
          console.debug("Autosave skipped because form is already saving.");
          return;
        }

        if (!this.hasUnsavedChanges()) {
          console.debug("Autosave skipped because no changes were found.");
          return;
        }

        console.debug("Autosaving.");

        this.disableManualSave();
        try {
          const token = await this.editor.api.save();
          this.updateSaveToken(token);
          await this.pushNewSaveTokenToMenuez(token);
        } catch (error) {
          flashError(
            "Something went wrong when attempting to (auto)save the design. Please refresh the page.",
            { scrollTo: true },
          );
          this.handleError(error)
        } finally {
          this.enableManualSave();
        }
      }, AUTOSAVE_INTERVAL);
    }

    updateSaveToken(token) {
      console.debug(`Updated save token: ${token}`);

      this.updateSaveTokenInputTarget(token);
    }

    async addToBasketCallback(saveToken, _thumbnailUrl) {
      this.updateSaveToken(saveToken);
      this.jumpToAfterSaveTarget();
    }

    updateSaveTokenInputTarget(saveToken) {
      this.saveTokenInputTarget.value = saveToken;
      this.saveTokenInputTarget.disabled = false;
    }

    async pushNewSaveTokenToMenuez(token) {
      Sentry.setExtras({ save_token_is_present: token.length > 0 });

      try {
        const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
        const response = await fetch(this.editorUpdateEndpointValue, {
          method: "PATCH",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({
            product_personalization: { printess_save_token: token },
          }),
        });

        if (!response.ok) {
          const body = await response.text();
          throw new Error(`Save Token could not be stored in Menuez - HTTP ${response.status}: ${body}`);
        }

        return response;
      } catch (error) {
        Sentry.setExtras({endpoint: this.editorUpdateEndpointValue});
        throw new Error("Save Token could not be stored in Menuez: " + error.message);
      }
    }

    jumpToAfterSaveTarget() {
      const target = document.querySelector(this.afterSaveTargetSelectorValue);

      if (!target) return;

      target.scrollIntoView({ behavior: "smooth" });
    }

    enableSaveOnSubmit() {
      this.element.addEventListener("submit", (event) => {
        this.saveAndSubmit(event);
      });
    }

    async saveAndSubmit(event) {
      event.preventDefault();

      this.ensureCommitInputIsPresent(event);

      // if we have a save token and there's nothing new to save, just submit the form and continue
      // with the usual menuez flow.
      if (this.hasSaveToken() && !this.hasUnsavedChanges()) {
        return this.element.submit();
      }

      // otherwise, validate and try to save
      await this.validateAndSave(event);
    }

    async validateAndSave(saveEvent) {
      const errors = this.editor.api.validate();
      console.debug(errors);

      if (errors.length === 0) {
        const token = await this.editor.api.save();

        if (token.length === 0) {
          flashError(
            "Something went wrong when attempting to save the design. Please try again.",
            { scrollTo: true },
          );
          return saveEvent.stopPropagation();
        } else {
          this.updateSaveToken(token);
          this.element.submit();
        }
      } else {
        // there are errors
        flashError("Validatie mislukt. Voltooi alle stappen.", {
          scrollTo: true,
        });

        this.resetEditor();

        return saveEvent.stopPropagation();
      }
    }

    // ProductPersonalizations#update has different routes to follow based on params[:commit]
    ensureCommitInputIsPresent(event) {
      if (
        event.submitter &&
        event.submitter.name &&
        event.submitter.name == "commit" &&
        event.submitter.value
      ) {
        let commit_input = this.element.querySelector(
          "input[type=hidden][name=commit]",
        );
        if (commit_input) {
          return;
        }

        commit_input = document.createElement("input");
        commit_input.name = "commit";
        commit_input.type = "hidden";
        commit_input.value = event.submitter.value;

        this.element.appendChild(commit_input);
      }
    }

    hasSaveToken() {
      return this.saveTokenInputTarget.value.length > 0;
    }

    resetEditor() {
      this.editor.api.clearSelection();
      this.editor.api.gotoFirstStep();
    }

    handlePrintessBridge(method, ...args) {
      const allowed = this.constructor.allowedPrintessBridgeMethods || [];

      if (allowed.includes(method) && typeof this[method] === "function") {
        this[method](...args);
      }
    }
  };
